import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setAccount, setInitialized, setUser, setIsLoggedIn } from '../redux/features/platformSlice';
import { EyeIcon, EyeOffIcon, UserIcon, ZapIcon } from '../helpers/Icons';
import platformService from '../services/platform/platformService';
import { clearAuthStorage, setAuthTokens } from '../services/platform/api';

const LOGO_URL = '/assets/img/logo-dark.png';

// Demo accounts
const DEMO_ACCOUNTS = [
  { name: 'Default Account', login: '2122679', password: '%iD9e8hy' },
];

const getQueryValue = (search, key) => {
  const rawSearch = String(search || '').replace(/^\?/, '');
  const pair = rawSearch
    .split('&')
    .map(part => part.split('='))
    .find(([name]) => name === key);

  if (!pair) return '';

  const rawValue = pair.slice(1).join('=').replace(/\+/g, ' ');
  try {
    return decodeURIComponent(rawValue);
  } catch (error) {
    return rawValue;
  }
};

const getEmbeddedCredentials = () => {
  const search = window.location.search;
  return {
    login: getQueryValue(search, 'login'),
    password: getQueryValue(search, 'password'),
  };
};

const AuthLoadingScreen = () => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0e1a 0%, #0d1835 52%, #09111f 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    color: '#f1f5f9',
  }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 96,
        height: 56,
        margin: '0 auto 18px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        filter: 'drop-shadow(0 0 28px rgba(59,130,246,0.45))',
      }}>
        <img src={LOGO_URL} alt="Core Prime LTD" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 7 }}>Opening Core Prime LTD</div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 22 }}>Securing your session and preparing live market data</div>
      <div style={{ width: 230, height: 4, borderRadius: 999, background: '#172033', overflow: 'hidden', margin: '0 auto' }}>
        <div style={{
          width: '42%',
          height: '100%',
          borderRadius: 999,
          background: 'linear-gradient(90deg, #2563eb, #22d3ee)',
          boxShadow: '0 0 18px rgba(34,211,238,0.55)',
        }} />
      </div>
    </div>
  </div>
);

const AuthFailedScreen = ({ message }) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0a0e1a 0%, #101827 55%, #120d1a 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    color: '#f1f5f9',
  }}>
    <div style={{
      width: '100%',
      maxWidth: 440,
      background: 'rgba(17,24,39,0.92)',
      border: '1px solid rgba(239,68,68,0.24)',
      borderRadius: 18,
      padding: '30px 28px',
      textAlign: 'center',
      boxShadow: '0 25px 70px rgba(0,0,0,0.48)',
    }}>
      <div style={{
        width: 58,
        height: 58,
        borderRadius: 16,
        margin: '0 auto 16px',
        background: 'rgba(239,68,68,0.12)',
        border: '1px solid rgba(239,68,68,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#f87171',
        fontSize: 28,
        fontWeight: 900,
      }}>
        !
      </div>
      <div style={{ fontSize: 21, fontWeight: 850, marginBottom: 8 }}>Terminal Access Unavailable</div>
      <div style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.65, margin: '0 auto', maxWidth: 340 }}>
        {message || 'We could not verify this trading account. Please reopen the terminal from your CRM or contact support if the issue continues.'}
      </div>
      <div style={{
        marginTop: 22,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'rgba(59,130,246,0.08)',
        border: '1px solid rgba(59,130,246,0.18)',
        color: '#93c5fd',
        fontSize: 11,
        fontWeight: 700,
      }}>
        The login window stayed hidden to protect the embedded experience.
      </div>
    </div>
  </div>
);

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const autoLoginStartedRef = useRef(false);
  const embeddedCredentials = getEmbeddedCredentials();
  const isEmbeddedAutoLogin = Boolean(embeddedCredentials.login && embeddedCredentials.password);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState('');
  const [autoLoginError, setAutoLoginError] = useState('');
  const [loading, setLoading] = useState(false);

  const performLogin = async (loginVal, passwordVal, options = {}) => {
    const silent = options.silent === true;
    if (!silent) setError('');
    setLoading(true);
    let loggedIn = false;
    try {
      const res = await platformService.login({
        login: String(loginVal),
        password: String(passwordVal),
      });
      const responseData = res.data || res;
      const token = responseData?.access_token || responseData?.accessToken;
      const refreshToken = responseData?.refresh_token || responseData?.refreshToken;

      if (token) {
        setAuthTokens(
          { accessToken: token, refreshToken },
          { clearMissingRefresh: true, reason: 'login' },
        );

        // Fetch complete account details with the new token
        const accountRes = await platformService.getDetails();
        if (accountRes?.success) {
          const userData = {
            ...accountRes.data.user,
          };
          const accountData = {
            ...accountRes.data.account,
          };

          localStorage.setItem('userData', JSON.stringify(userData));
          localStorage.setItem('accountData', JSON.stringify(accountData));

          dispatch(setUser(userData));
          dispatch(setAccount(accountData));
          dispatch(setInitialized(false));
          dispatch(setIsLoggedIn(true));
          loggedIn = true;
          navigate('/dashboard', { replace: true });
        } else {
          clearAuthStorage('profile-failed');
          const message = accountRes?.message || 'Failed to retrieve account details.';
          if (silent) setAutoLoginError(message);
          else setError(message);
        }
      } else {
        const message = res?.message || 'Invalid login or password.';
        if (silent) setAutoLoginError(message);
        else setError(message);
      }
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Login failed. Please try again.';
      if (silent) setAutoLoginError(message);
      else setError(message);
    }
    setLoading(false);
    return loggedIn;
  };

  useEffect(() => {
    const loginParam = embeddedCredentials.login;
    const passwordParam = embeddedCredentials.password;

    if (!loginParam || !passwordParam || autoLoginStartedRef.current) return;

    autoLoginStartedRef.current = true;
    setLogin(loginParam);
    setPassword(passwordParam);
    window.history.replaceState({}, document.title, '/login');
    performLogin(loginParam, passwordParam, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    await performLogin(login, password);
  };

  const handleDemoLogin = async (acct) => {
    setLogin(acct.login);
    setPassword(acct.password);
    await performLogin(acct.login, acct.password);
  };

  if (isEmbeddedAutoLogin) {
    if (autoLoginError) {
      return <AuthFailedScreen message={autoLoginError} />;
    }

    return <AuthLoadingScreen />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e1a 0%, #0d1835 50%, #0a1428 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '16px', fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
    }}>
      {/* Background glow effects */}
      <div style={{ position: 'fixed', top: '-10%', left: '30%', width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', bottom: '-10%', right: '20%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.07) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 300, height: 120, filter: 'drop-shadow(0 0 24px rgba(59,130,246,0.38))', marginBottom: 16 }}>
            <img src={LOGO_URL} alt="Core Prime LTD" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          {/* <h1 style={{ fontSize: 26, fontWeight: 800, color: '#f1f5f9', margin: '0 0 4px', letterSpacing: 0 }}>Core Prime LTD</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>Professional Trading Platform</p> */}
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(17,24,39,0.9)', border: '1px solid rgba(30,45,71,0.8)',
          borderRadius: 16, padding: '28px 28px',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', margin: '0 0 6px' }}>Sign In</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 24px' }}>Enter your trading account credentials</p>

          <form onSubmit={handleLogin}>
            {/* Login input */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8899aa', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Account Login</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <UserIcon s={15} c="#4b5563" />
                </div>
                <input
                  type="text"
                  value={login}
                  onChange={e => setLogin(e.target.value)}
                  placeholder="Enter account login"
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#1a2235', border: '1px solid #1e2d47',
                    borderRadius: 9, padding: '11px 12px 11px 38px',
                    color: '#f1f5f9', fontSize: 13, outline: 'none',
                    transition: 'border-color 0.2s',
                    fontFamily: 'inherit',
                  }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e => e.target.style.borderColor = '#1e2d47'}
                />
              </div>
            </div>

            {/* Password input */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8899aa', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                  <ZapIcon s={15} c="#4b5563" />
                </div>
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter password"
                  required
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    background: '#1a2235', border: '1px solid #1e2d47',
                    borderRadius: 9, padding: '11px 40px 11px 38px',
                    color: '#f1f5f9', fontSize: 13, outline: 'none',
                    transition: 'border-color 0.2s',
                    fontFamily: 'inherit',
                  }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e => e.target.style.borderColor = '#1e2d47'}
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
                >
                  {showPass ? <EyeOffIcon s={15} c="#6b7a90" /> : <EyeIcon s={15} c="#6b7a90" />}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#ef4444', fontSize: 12, marginBottom: 16 }}>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%', padding: '13px',
                borderRadius: 10, border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#1e3a5f' : 'linear-gradient(135deg, #2563eb, #4f46e5)',
                color: '#fff', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit',
                transition: 'all 0.2s',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(59,130,246,0.35)',
              }}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0' }}>
            <div style={{ flex: 1, height: 1, background: '#1e2d47' }} />
            <span style={{ fontSize: 11, color: '#4b5563' }}>
              Try with correct credentials
            </span>
            <div style={{ flex: 1, height: 1, background: '#1e2d47' }} />
          </div>

          {/* Demo account quick-fill buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {DEMO_ACCOUNTS.map(acct => (
              <button
                key={acct.login}
                onClick={() => handleDemoLogin(acct)}
                style={{
                  width: '100%', padding: '10px 14px',
                  borderRadius: 9, border: '1px solid #1e2d47', cursor: 'pointer',
                  background: '#1a2235', color: '#f1f5f9', fontFamily: 'inherit',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'border-color 0.2s, background 0.2s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#1e2d47'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = '#1e2d47'; e.currentTarget.style.background = '#1a2235'; }}
              >
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{acct.name}</div>
                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>Login: {acct.login}</div>
                </div>
                <span style={{ fontSize: 10, color: '#3b82f6', fontWeight: 600 }}>USE →</span>
              </button>
            ))}
          </div>
        </div>

        {/* <p style={{ textAlign: 'center', fontSize: 11, color: '#374151', marginTop: 20 }}>
          Demo platform — no real funds involved
        </p> */}
      </div>
    </div>
  );
};

export default Login;
